import { io, Socket } from 'socket.io-client';

export interface WebSocketNotification {
  id: number;
  type: string;
  title: string;
  message: string;
  priority: string;
  createdAt: string;
}

export interface WebSocketEventHandlers {
  onNewNotification?: (notification: WebSocketNotification) => void;
  onUnreadCountUpdate?: (data: { count: number }) => void;
  onNotificationMarkedRead?: (data: { notificationId: number; success: boolean }) => void;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
}

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectInterval: number = 5000; // 5 segundos
  private maxReconnectAttempts: number = 10;
  private reconnectAttempts: number = 0;
  private isManualDisconnect: boolean = false;
  private eventHandlers: WebSocketEventHandlers = {};

  /**
   * Conectar ao WebSocket server
   */
  connect(token: string, handlers?: WebSocketEventHandlers): void {
    if (this.socket?.connected) {
      return;
    }

    if (handlers) {
      this.eventHandlers = handlers;
    }

    const serverUrl = process.env.REACT_APP_WS_URL || process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

    this.socket = io(serverUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: this.reconnectInterval,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 20000,
    });

    this.setupEventListeners();
  }

  /**
   * Configurar event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Eventos de conexão
    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.eventHandlers.onConnect?.();
    });

    this.socket.on('disconnect', (reason: string) => {
      this.eventHandlers.onDisconnect?.(reason);
      
      // Tentar reconectar se não foi desconexão manual
      if (!this.isManualDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.scheduleReconnect();
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('Erro de conexão WebSocket:', error);
      this.eventHandlers.onError?.(error);
      this.scheduleReconnect();
    });

    // Eventos de notificação
    this.socket.on('newNotification', (notification: WebSocketNotification) => {
      this.eventHandlers.onNewNotification?.(notification);
      
      // Mostrar notificação do navegador se permitido
      this.showBrowserNotification(notification);
    });

    this.socket.on('unreadCountUpdate', (data: { count: number }) => {
      this.eventHandlers.onUnreadCountUpdate?.(data);
    });

    this.socket.on('notificationMarkedRead', (data: { notificationId: number; success: boolean }) => {
      this.eventHandlers.onNotificationMarkedRead?.(data);
    });

    // Eventos de manutenção da conexão
    this.socket.on('pong', () => {
      // Resposta ao ping - conexão está viva
    });

    // Enviar ping periodicamente para manter conexão viva
    this.startPingInterval();
  }

  /**
   * Agendar tentativa de reconexão
   */
  private scheduleReconnect(): void {
    if (this.isManualDisconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectInterval * this.reconnectAttempts, 30000); // Max 30s

    setTimeout(() => {
      if (!this.socket?.connected && !this.isManualDisconnect) {
        this.socket?.connect();
      }
    }, delay);
  }

  /**
   * Iniciar ping interval para manter conexão viva
   */
  private startPingInterval(): void {
    if (!this.socket) return;

    setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('ping');
      }
    }, 30000); // Ping a cada 30 segundos
  }

  /**
   * Mostrar notificação do navegador
   */
  private showBrowserNotification(notification: WebSocketNotification): void {
    if (!('Notification' in window)) {
      return;
    }

    if (Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        tag: `notification-${notification.id}`,
        requireInteraction: notification.priority === 'HIGH' || notification.priority === 'URGENT',
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          this.showBrowserNotification(notification);
        }
      });
    }
  }

  /**
   * Marcar notificação como lida
   */
  markNotificationAsRead(notificationId: number): void {
    if (this.socket?.connected) {
      this.socket.emit('markNotificationRead', notificationId);
    }
  }

  /**
   * Solicitar contagem de não lidas
   */
  requestUnreadCount(): void {
    if (this.socket?.connected) {
      this.socket.emit('requestUnreadCount');
    }
  }

  /**
   * Atualizar event handlers
   */
  updateEventHandlers(handlers: WebSocketEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  /**
   * Desconectar manualmente
   */
  disconnect(): void {
    this.isManualDisconnect = true;
    this.socket?.disconnect();
    this.socket = null;
  }

  /**
   * Verificar se está conectado
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Obter ID da conexão
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /**
   * Obter instância do socket (para listeners customizados)
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Obter estatísticas da conexão
   */
  getConnectionStats(): {
    connected: boolean;
    socketId?: string;
    reconnectAttempts: number;
    maxReconnectAttempts: number;
  } {
    return {
      connected: this.isConnected(),
      socketId: this.getSocketId(),
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
    };
  }
}

// Singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;
